import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CITATION_FORMATS,
  citationFilename,
  formatCitation,
  formatVolumeCitations,
  stripLatex,
} from '../data/citations.js';

/**
 * The "Cite" dialog.
 *
 * Nothing here talks to the network: the citation text is derived from the
 * article record the page already has, so the dialog opens instantly and works
 * offline. Readers who copy (most of them) never trigger a download at all.
 *
 * Pass a single `article` for one reference, or `articles` for a whole volume.
 * `siblings` is the volume the single article belongs to — used only to give a
 * repeated `Surname_Year` cite key its "a" / "b" suffix.
 */
const CiteModal = ({ article, articles, siblings, year, onClose }) => {
  const [formatId, setFormatId] = useState(CITATION_FORMATS[0].id);
  const [copied, setCopied] = useState(false);

  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  const copyTimer = useRef(null);

  const isVolume = Boolean(articles);
  const format = CITATION_FORMATS.find((f) => f.id === formatId);

  const text = useMemo(
    () =>
      isVolume
        ? formatVolumeCitations(articles, year, formatId)
        : formatCitation(article, year, formatId, siblings),
    [isVolume, articles, article, siblings, year, formatId]
  );

  const filename = isVolume
    ? `ejaam-${year}.${format.extension}`
    : citationFilename(article, year, formatId);

  const heading = isVolume
    ? `Cite volume ${year}`
    : stripLatex(article && article.title);

  // Switching tabs invalidates the "Copied" confirmation.
  useEffect(() => {
    setCopied(false);
  }, [formatId]);

  // Remember who opened the dialog so focus can go back there on close, and
  // stop the page behind from scrolling while it is open.
  useEffect(() => {
    openerRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    if (dialogRef.current) dialogRef.current.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      if (openerRef.current && openerRef.current.focus) openerRef.current.focus();
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  // Escape closes; Tab is kept inside the dialog.
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(
        'button, [href], textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      // Clipboard API needs a secure context and a permission the browser may
      // withhold; the old selection trick still works everywhere it doesn't.
      const scratch = document.createElement('textarea');
      scratch.value = text;
      scratch.setAttribute('readonly', '');
      scratch.style.position = 'fixed';
      scratch.style.opacity = '0';
      document.body.appendChild(scratch);
      scratch.select();
      try {
        document.execCommand('copy');
      } catch (ignored) {
        return; // Nothing left to try — leave the text on screen to select by hand.
      } finally {
        document.body.removeChild(scratch);
      }
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([text], { type: format.mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Safari needs the object URL to outlive the click by a tick.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return createPortal(
    <div
      className="cite-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="cite-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cite-dialog-title"
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={handleKeyDown}
      >
        <div className="cite-dialog__head">
          <div>
            <p className="cite-dialog__eyebrow">Cite this {isVolume ? 'volume' : 'article'}</p>
            <h2 id="cite-dialog-title">{heading}</h2>
          </div>
          <button type="button" className="cite-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="cite-tabs" role="tablist" aria-label="Citation format">
          {CITATION_FORMATS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              id={`cite-tab-${option.id}`}
              aria-selected={option.id === formatId}
              aria-controls="cite-panel"
              className={`cite-tab${option.id === formatId ? ' is-active' : ''}`}
              onClick={() => setFormatId(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div
          className="cite-panel"
          id="cite-panel"
          role="tabpanel"
          aria-labelledby={`cite-tab-${formatId}`}
        >
          <pre className="cite-preview">{text}</pre>
          <p className="cite-hint">{format.hint}</p>
        </div>

        <div className="cite-actions">
          <button type="button" className="cite-btn cite-btn--ghost" onClick={handleCopy}>
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" className="cite-btn cite-btn--solid" onClick={handleDownload}>
            Download .{format.extension}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CiteModal;
